import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Chip,
  Avatar,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  IconButton,
  Tooltip,
  LinearProgress,
  alpha
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Person as PersonIcon, School as SchoolIcon, Payments as PaymentsIcon, Print as PrintIcon, LocalOffer as LocalOfferIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon, Pending as PendingIcon, WatchLater as WatchLaterIcon } from '@mui/icons-material';
import SecretarySidebar from '../../components/SecretarySidebar';
import { format } from 'date-fns';
import fr from 'date-fns/locale/fr';

// Fonction pour obtenir l'année scolaire courante
function getCurrentSchoolYear() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month >= 9) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
}

const StudentDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [student, setStudent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discounts, setDiscounts] = useState<any[]>([]);
  // Ajout pour le filtre année scolaire
  const [schoolYear, setSchoolYear] = useState<string>(getCurrentSchoolYear());
  const availableYears = ['2023-2024', '2024-2025', '2025-2026'];
  const [schedule, setSchedule] = useState<any>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchStudentDetails = async () => {
      if (!isMounted) return;
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const [studentData, discountsData] = await Promise.all([
          axios.get(`https://lapetiteacademie.ci/api/students/${id}/details?school_year=${schoolYear}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`https://lapetiteacademie.ci/api/discounts/student/${id}`)
        ]);
        if (isMounted) {
          setStudent(studentData.data);
          setDiscounts(discountsData.data);
        }
      } catch (err: any) {
        if (isMounted) setError(err.response?.data?.message || "Erreur lors de la récupération des détails de l'élève.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const fetchSchedule = async () => {
      if (!isMounted) return;
      setScheduleLoading(true);
      try {
        const token = localStorage.getItem('token');
        const scheduleData = await axios.get(`https://lapetiteacademie.ci/api/payments/schedule/${id}?school_year=${schoolYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isMounted) setSchedule(scheduleData.data);
      } catch (err: any) {
        console.error("Erreur lors du chargement de l'échéancier:", err);
      } finally {
        if (isMounted) setScheduleLoading(false);
      }
    };

    if (id) {
      fetchStudentDetails();
      fetchSchedule();
    }
    
    return () => {
      isMounted = false;
    };
  }, [id, schoolYear]);

  const handlePrint = () => {
    window.print();
  };
  
  const DetailItem = ({ label, value, icon }: { label: string; value: any; icon?: React.ReactElement }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
            {icon && <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>{icon}</Avatar>}
            <Box>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{value || 'N/A'}</Typography>
            </Box>
        </Box>
    </Grid>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: theme.palette.background.default }}>
      <SecretarySidebar />
      <Container maxWidth="lg" sx={{ p: 3, '@media print': { p: 0 } }}>
        {/* Sélecteur d'année scolaire */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="body2" sx={{ mr: 2, fontWeight: 600 }}>Année scolaire :</Typography>
          <select value={schoolYear} onChange={e => setSchoolYear(e.target.value)} style={{ fontSize: 16, padding: '4px 12px', borderRadius: 6, border: '1px solid #1976d2' }}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Box>
        <Paper sx={{ p: 3, borderRadius: 4, boxShadow: 3, '@media print': { boxShadow: 'none', p: 2 } }}>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>}
          {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
          {student && (
            <>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {student.child_photo ? (
                    <Box sx={{ position: 'relative', mr: 2 }}>
                      <img
                        src={`https://lapetiteacademie.ci/api/students/photo/${student.child_photo}`}
                        alt="Photo de l'enfant"
                        style={{ 
                          width: 120, 
                          height: 120, 
                          objectFit: 'cover', 
                          borderRadius: '50%',
                          border: `3px solid ${theme.palette.primary.main}`,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }}
                        onError={(e) => {
                          console.error('❌ Erreur de chargement de la photo:', student.child_photo);
                          console.error('🔗 URL tentée:', `https://lapetiteacademie.ci/api/students/photo/${student.child_photo}`);
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.setAttribute('style', 'display: block');
                        }}
                        onLoad={() => {
                          console.log('✅ Photo chargée avec succès:', student.child_photo);
                        }}
                      />
                      <Avatar 
                        sx={{ 
                          width: 120, 
                          height: 120, 
                          bgcolor: 'primary.main', 
                          fontSize: '3rem',
                          display: 'none',
                          border: `3px solid ${theme.palette.primary.main}`,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }}
                      >
                        {student.first_name?.[0]}{student.last_name?.[0]}
                      </Avatar>
                    </Box>
                  ) : (
                    <Avatar sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: 'primary.main', 
                      mr: 2, 
                      fontSize: '3rem',
                      border: `3px solid ${theme.palette.primary.main}`,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}>
                      {student.first_name?.[0]}{student.last_name?.[0]}
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                      {student.first_name} {student.last_name}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      Matricule: {student.registration_number || 'Non défini'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, '@media print': { display: 'none' } }}>
                  <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                    Retour
                  </Button>
                   <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
                    Imprimer
                  </Button>
                </Box>
              </Box>

              {/* Personal Info */}
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    <PersonIcon /> Informations Personnelles
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  
                  {/* Photo de l'enfant */}
                  {student.child_photo && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <img
                          src={`https://lapetiteacademie.ci/api/students/photo/${student.child_photo}`}
                          alt="Photo de l'enfant"
                          style={{ 
                            width: 200, 
                            height: 200, 
                            objectFit: 'cover', 
                            borderRadius: '10px',
                            border: `3px solid ${theme.palette.primary.main}`,
                            boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
                          }}
                          onError={(e) => {
                            console.error('❌ Erreur de chargement de la photo:', student.child_photo);
                            console.error('🔗 URL tentée:', `https://lapetiteacademie.ci/api/students/photo/${student.child_photo}`);
                            e.currentTarget.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('✅ Photo chargée avec succès:', student.child_photo);
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Photo de l'enfant
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <Grid container spacing={1}>
                    <DetailItem label="Date de naissance" value={student.date_of_birth ? format(new Date(student.date_of_birth), 'dd MMMM yyyy', { locale: fr }) : 'N/A'} />
                    <DetailItem label="Genre" value={student.gender} />
                    <DetailItem label="Adresse" value={student.address} />
                    <DetailItem label="Ville" value={student.city} />
                    
                    {/* Ajout mange à la cantine */}
                    {student.cantine ? (
                      <DetailItem label="L'enfant mange à la cantine" value={student.eats_at_cantine === 1 || student.eats_at_cantine === true || student.eats_at_cantine === '1' ? 'Oui' : 'Non'} />
                    ) : null}
                    {/* Allergie éventuelle */}
                    {student.cantine && student.allergy && (
                      <DetailItem label="Allergie(s) éventuelle(s)" value={<span style={{ color: '#d32f2f', fontWeight: 500 }}>{student.allergy}</span>} />
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Academic Info */}
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    <SchoolIcon /> Informations Académiques
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1}>
                    <DetailItem label="Classe actuelle" value={<Chip label={student.classe_name || 'Non assigné'} color="primary" />} />
                    <DetailItem label="Code Parent" value={<Chip label={student.parent_code || 'N/A'} color="secondary" variant="outlined" />} />
                  </Grid>
                </CardContent>
              </Card>

              {/* Financial Info */}
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    <PaymentsIcon /> Informations Financières
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                   <Grid container spacing={3} sx={{ my: 2 }}>
                        <Grid item xs={12} sm={4}>
                           <Paper sx={{p: 2, textAlign: 'center', background: 'linear-gradient(45deg, #e3f2fd, #bbdefb)'}}>
                                <Typography variant="h6">Total à Payer</Typography>
                                <Typography variant="h5" sx={{fontWeight: 'bold'}}>{(student.total_due || 0).toLocaleString('fr-FR')} F CFA</Typography>
                           </Paper>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Paper sx={{p: 2, textAlign: 'center', background: 'linear-gradient(45deg, #e8f5e9, #c8e6c9)'}}>
                                <Typography variant="h6">Total Payé</Typography>
                                <Typography variant="h5" sx={{fontWeight: 'bold', color: 'success.dark'}}>{(student.total_paid || 0).toLocaleString('fr-FR')} F CFA</Typography>
                           </Paper>
                        </Grid>
                         <Grid item xs={12} sm={4}>
                            <Paper sx={{p: 2, textAlign: 'center', background: 'linear-gradient(45deg, #ffebee, #ffcdd2)'}}>
                                <Typography variant="h6">Solde Restant</Typography>
                                <Typography variant="h5" sx={{fontWeight: 'bold', color: 'error.dark'}}>{((student.total_due || 0) - (student.total_paid || 0)).toLocaleString('fr-FR')} F CFA</Typography>
                           </Paper>
                        </Grid>
                    </Grid>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>Historique des paiements</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{backgroundColor: 'grey.200'}}>
                          <TableCell sx={{fontWeight: 'bold'}}>Date</TableCell>
                          <TableCell sx={{fontWeight: 'bold'}} align="right">Montant</TableCell>
                          <TableCell sx={{fontWeight: 'bold'}}>Statut</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {student.payments && student.payments.length > 0 ? (
                          student.payments.map((p: any) => (
                            <TableRow key={p.id}>
                              <TableCell>{format(new Date(p.payment_date), 'dd/MM/yyyy HH:mm', { locale: fr })}</TableCell>
                              <TableCell align="right">{p.amount.toLocaleString('fr-FR')} F CFA</TableCell>
                              <TableCell>
                                <Chip label={p.status} color={p.status === 'completed' || p.status === 'paid' ? 'success' : 'warning'} size="small" />
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">Aucun paiement enregistré</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Échéancier Section */}
                  <Box sx={{ mt: 5, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <PaymentsIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '0.5px' }}>
                      Échéancier des versements
                    </Typography>
                  </Box>
                  {scheduleLoading ? <CircularProgress size={24} /> : schedule && schedule.schedule ? (
                    <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                      <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                          <TableRow sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
                            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Tranche de Paiement</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Date Limite</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }} align="right">Montant Attendu</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }} align="right">Déjà Couvert</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }} align="right">Reste à Payer</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, textAlign: 'center' }}>Statut</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {schedule.schedule.map((item: any, idx: number) => {
                            const isPaid = item.status === 'paid';
                            const isOverdue = item.status === 'overdue';
                            const isPartial = item.status === 'partial';
                            const progress = item.expected > 0 ? Math.min(100, Math.round((item.paid / item.expected) * 100)) : 0;
                            
                            return (
                              <TableRow 
                                key={idx} 
                                sx={{ 
                                  '&:last-child td, &:last-child th': { border: 0 },
                                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                                  transition: 'background-color 0.2s ease'
                                }}
                              >
                                <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                                <TableCell>
                                  {item.dueDate ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: isOverdue ? 'error.main' : 'text.secondary' }}>
                                      {isOverdue && <WarningIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                                      {format(new Date(item.dueDate), 'dd/MM/yyyy', { locale: fr })}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 500 }}>{(item.expected || 0).toLocaleString('fr-FR')} F CFA</TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: progress === 100 ? 'success.main' : 'text.primary' }}>
                                      {(item.paid || 0).toLocaleString('fr-FR')} F CFA
                                    </Typography>
                                    <Box sx={{ width: '80%', mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary', fontSize: '0.65rem' }}>{progress}%</Typography>
                                      <LinearProgress 
                                        variant="determinate" 
                                        value={progress} 
                                        sx={{ 
                                          flexGrow: 1, 
                                          height: 6, 
                                          borderRadius: 3,
                                          bgcolor: alpha(progress === 100 ? theme.palette.success.main : theme.palette.primary.main, 0.1),
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: progress === 100 ? 'success.main' : 'primary.main',
                                            borderRadius: 3
                                          }
                                        }} 
                                      />
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ color: item.remaining > 0 ? 'error.main' : 'text.disabled', fontWeight: item.remaining > 0 ? 700 : 400 }}>
                                  {(item.remaining || 0).toLocaleString('fr-FR')} F CFA
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    icon={isPaid ? <CheckCircleIcon /> : isOverdue ? <WarningIcon /> : isPartial ? <WatchLaterIcon /> : <PendingIcon />}
                                    label={isPaid ? 'Soldé' : isPartial ? 'Partiel' : isOverdue ? 'En retard' : 'En attente'} 
                                    sx={{
                                      fontWeight: 'bold',
                                      px: 1,
                                      bgcolor: isPaid ? alpha(theme.palette.success.main, 0.1) : isOverdue ? alpha(theme.palette.error.main, 0.1) : isPartial ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                      color: isPaid ? 'success.dark' : isOverdue ? 'error.dark' : isPartial ? 'warning.dark' : 'text.secondary',
                                      border: `1px solid ${isPaid ? alpha(theme.palette.success.main, 0.2) : isOverdue ? alpha(theme.palette.error.main, 0.2) : isPartial ? alpha(theme.palette.warning.main, 0.2) : alpha(theme.palette.grey[500], 0.2)}`
                                    }}
                                    size="small" 
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Échéancier non disponible pour cette année.</Typography>
                  )}
                </CardContent>
              </Card>

              
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default StudentDetails; 